import React, { useRef, useState, useEffect } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { mediaQueries } from 'shared/styles';
import { Button } from 'shared/components';
import useCart from 'shared/hooks/use-cart';
import { useCheckout } from 'checkout/context';
import PublicEnv from 'shared/utils/public-env';
import { DefaultInput, ExpirationDateInput, CvvInput } from 'shared/components/text-input-with-label';
import {
  AmplitudeCategory,
  AmplitudeEvents,
  useAmplitude,
  AmplitudePaymentFormEventDescriptions,
} from 'shared/hooks/use-amplitude';
import { fonts } from 'shared/themes.js';

import { useCheckoutCredentials } from 'checkout/components/sections/payment/use-checkout-credentials';

const isProduction = PublicEnv.appEnv === 'production';

const baseMonerisIframeUrl = isProduction
  ? 'https://www3.moneris.com/HPPtoken/index.php'
  : 'https://esqa.moneris.com/HPPtoken/index.php';

const isBrowser = () => typeof window !== 'undefined';
const mobile = isBrowser() && window.matchMedia(mediaQueries.tablet).matches;
const monerisStyles = {
  enable_exp: '0',
  enable_cvd: '0',
  display_labels: '2',
  css_body: {
    'font-family': _.join(fonts, ','),
    color: 'rgb(93, 102, 109)',
    'font-weight': 400,
    'font-size': '16px',
    '-webkit-font-smoothing': 'antialiased',
  },
  css_textbox: {
    border: '1px solid #caced4',
    'border-radius': '4px',
    height: '45px',
    'font-size': mobile ? '16px' : '13px',
    padding: '18px',
    'font-weight': 400,
    'background-color': '#fcfdfe',
    color: 'rgb(93, 102, 109)',
  },
  css_textbox_pan: {
    width: '100%',
  },
};

const flattenStyles = (styles) => _.map(styles, (value, key) => `${key}:${value}`).join(';');
const styleParams = _.map(monerisStyles, (value, key) => {
  const styles = _.isObject(value) ? flattenStyles(value) : value;
  return `${encodeURIComponent(key)}=${encodeURIComponent(styles)}`;
}).join('&');

const monerisSuccessCode = '001';
const messagesForMonerisErrorCodes = {
  888: 'postal code',
  940: 'Invalid profile id (on tokenization request)',
  941: 'Error generating token',
  942: 'Invalid Profile ID, or source URL',
  943: 'card number',
  944: 'expiration date',
  945: 'CVV',
};

function errorMessage(codeSet) {
  // convert set to array
  const codes = Array.from(codeSet);

  if (!codes.length) {
    return '';
  }

  const incorrectMonerisProfileIDError = _.includes(codes, 942);

  if (incorrectMonerisProfileIDError) {
    return `An invalid Profile ID has been used.  Please update settings and try again.`;
  }

  const messages = _.map(codes, (code) => messagesForMonerisErrorCodes[code]);

  if (codes.length === 1) {
    return `Your ${messages[0]} is invalid. Please update and try again.`;
  }
  if (codes.length === 2) {
    return `Your ${messages[0]} and ${messages[1]} are invalid. Please update and try again.`;
  }
  // codes.length is greater than or equal to 3
  const lastWord = messages[messages.length - 1];
  const allTheRest = messages.slice(0, -1);
  return `Your ${_.join(allTheRest, ', ')}, and ${lastWord} are invalid. Please update and try again.`;
}

const validatePostalCode = (postalCode) => postalCode && postalCode.length > 4;

const validateExpiry = (expiryDate) => {
  if (!expiryDate) {
    return false;
  }
  const today = new Date().getTime();
  const parsedInputDate = _.split(expiryDate, '/');
  const inputDate = new Date(`20${parsedInputDate[1]}`, `${+parsedInputDate[0] - 1}`).getTime();
  return inputDate > today;
};

const validateCvv = (cvv) => cvv && cvv.length > 2;

export function HostedTokenizationForm(props) {
  const amplitude = useAmplitude();
  const Cart = useCart();
  const Checkout = useCheckout();
  const iframeRef = useRef();
  const [monerisResponse, setMonerisResponse] = useState();
  const { onSave, handleError } = props;
  const onSaveRef = useRef(onSave);
  const [credentials] = useCheckoutCredentials('moneris');
  const htProfileId = credentials?.profileId;
  const [errorMessages, setErrorMessages] = useState(new Set());
  const [isSubmit, setSubmit] = useState(false);

  useEffect(() => {
    const handleMonerisResponse = (e) => {
      if (_.endsWith(_.trim(e.origin), '.moneris.com')) {
        const responseData = JSON.parse(e.data);
        setMonerisResponse(responseData);
        const successful = responseData?.responseCode === monerisSuccessCode;
        if (successful) {
          Checkout.removeError({ path: 'payment.monerisPAN' });
          Cart.monerisInput.cardToken = responseData?.dataKey;
        } else {
          Checkout.addErrors([{ card: 'payment' }, { path: 'payment.monerisPAN' }]);
          Cart.monerisInput.cardToken = null;
        }
      }
    };

    if (isSubmit) {
      // fixes iframe sending postMessage on focus for iOS Chrome
      window.addEventListener('message', handleMonerisResponse, false);
    }

    return () => {
      window.removeEventListener('message', handleMonerisResponse, false);
    };
  }, [Cart.monerisInput.cardToken, Checkout, isSubmit]);

  useEffect(() => {
    const newErrorMessages = new Set(errorMessages);
    if (isSubmit) {
      if (monerisResponse?.responseCode === '942') {
        newErrorMessages.add(942);
        window.Rollbar.error(`Invalid Moneris Profile ID, or source URL`);
      } else {
        newErrorMessages.delete(942);

        if (validatePostalCode(Cart.monerisInput.postalCode)) {
          newErrorMessages.delete(888);
        } else {
          newErrorMessages.add(888);
        }

        if (validateCvv(Cart.monerisInput.cvv)) {
          newErrorMessages.delete(945);
        } else {
          newErrorMessages.add(945);
        }

        if (validateExpiry(Cart.monerisInput.expiryDate)) {
          newErrorMessages.delete(944);
        } else {
          newErrorMessages.add(944);
        }

        // check Moneris field for errors
        if (Checkout.getError({ path: 'payment.monerisPAN' }).error) {
          newErrorMessages.add(943);
        } else if (
          !Checkout.getError({ path: 'payment.monerisPAN' }).error &&
          monerisResponse?.responseCode === '001'
        ) {
          newErrorMessages.delete(943);
        }
      }
    }

    if (!_.isEqual(newErrorMessages, errorMessages)) {
      setErrorMessages(newErrorMessages);
      handleError(errorMessage(newErrorMessages));
    }

    if (
      !newErrorMessages.length &&
      monerisResponse?.responseCode === '001' &&
      validatePostalCode(Cart.monerisInput.postalCode) &&
      validateCvv(Cart.monerisInput.cvv) &&
      validateExpiry(Cart.monerisInput.expiryDate)
    ) {
      amplitude.log(AmplitudeEvents.payments.moneris.onSave, {
        description: AmplitudePaymentFormEventDescriptions.moneris.onSaveSuccess,
        category: AmplitudeCategory.paymentsMoneris,
      });

      setSubmit(false);

      onSaveRef.current();
    } else if (newErrorMessages.length) {
      amplitude.log(AmplitudeEvents.payments.moneris.onError, {
        description: `${AmplitudePaymentFormEventDescriptions.moneris.onError}${_.join(
          _.map(newErrorMessages, (v) => messagesForMonerisErrorCodes[v]),
          ', '
        )}`,
        category: AmplitudeCategory.paymentsMoneris,
      });
    }
  }, [onSaveRef.current, monerisResponse, Checkout, Cart.monerisInput, errorMessages]);

  if (!htProfileId) {
    return <StyledButton loading>Loading Moneris Payments Configuration</StyledButton>;
  }

  const monerisIframeUrl = `${baseMonerisIframeUrl}?id=${htProfileId}&${styleParams}`;

  const updatePostalCode = (event) => {
    const value = event.target.value.replace(/\s+/g, '');
    Cart.monerisInput.postalCode = value;
  };

  const updateCvv = (event) => {
    const value = event.target.value.replace(/\s+/g, '');
    Cart.monerisInput.cvv = value;
  };

  const updateExpiration = (event) => {
    const value = event.target.value.replace(/\s+/g, '');
    Cart.monerisInput.expiryDate = value;
  };

  const doMonerisSubmit = () => {
    setSubmit(true);
    amplitude.log(AmplitudeEvents.payments.moneris.onSave, {
      category: AmplitudeCategory.paymentsMoneris,
    });

    const iframeContentWindow = iframeRef?.current?.contentWindow;
    iframeContentWindow.postMessage('tokenize', baseMonerisIframeUrl);
    return false;
  };

  return (
    <div
      style={{
        background: '#f0f3f6',
        borderRadius: 8,
        padding: 18,
        border: 'none',
        marginTop: 12,
      }}
    >
      <HeaderText>Enter credit card information</HeaderText>
      <InputsContainer>
        <iframe
          title='Card Payment Information Form'
          ref={iframeRef}
          id='monerisFrame'
          frameBorder='0'
          height='45'
          width='100%'
          src={monerisIframeUrl}
        />
      </InputsContainer>
      <InputsContainer>
        <ExpirationDateInput
          containerClassName='expirationDate'
          label='Exp Date'
          aria-label='expiration date input'
          data-cy='expiration-date-input'
          data-test='expiration-date-input'
          mr='8px'
          width='calc(100% - 8px)'
          format='##/##'
          placeholder='mm/yy'
          onValueAfterError={() => Checkout.removeError({ path: 'payment.monerisExpirationDate' })}
          error={Checkout.getError({ path: 'payment.monerisExpirationDate' }).error}
          errorMessage={Checkout.getError({ path: 'payment.monerisExpirationDate' }).message}
          onBlur={updateExpiration}
        />
        <CvvInput
          containerClassName='cvvInput'
          placeholder='CVV'
          label='CVV'
          aria-label='cvv input'
          data-cy='cvv-input'
          data-test='cvv-input'
          ml='8px'
          width='calc(100% - 8px)'
          onBlur={updateCvv}
          onValueAfterError={() => Checkout.removeError({ path: 'payment.monerisCvv' })}
          error={Checkout.getError({ path: 'payment.monerisCvv' }).error}
          errorMessage={Checkout.getError({ path: 'payment.monerisCvv' }).message}
        />
      </InputsContainer>
      <InputsContainer>
        <FullDefaultInput
          id='monerisPostalCode'
          name='monerisPostalCode'
          label='Postal Code'
          aria-label='Postal code input'
          onValueAfterError={() => Checkout.removeError({ path: 'payment.monerisPostalCode' })}
          error={Checkout.getError({ path: 'payment.monerisPostalCode' }).error}
          errorMessage={Checkout.getError({ path: 'payment.monerisPostalCode' }).message}
          onBlur={updatePostalCode}
        />
      </InputsContainer>
      <StyledButton onClick={doMonerisSubmit}>Save</StyledButton>
    </div>
  );
}

const StyledButton = styled(Button)`
  margin-top: 25px;
  width: 73px;
  height: 33px;
  margin-right: 15px;
`;

const HeaderText = styled.div`
  font-size: 13px;
  line-height: 15px;
  color: ${(props) => (props.error ? '#e25241' : '#5d666d')};
`;

const InputsContainer = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const FullDefaultInput = styled(DefaultInput)`
  &.MuiFormControl-root {
    width: 100%;
  }
`;
